import { useState, useRef, useCallback } from "react";

/**
 * useVideoMovementAnalyzer — runs MediaPipe Pose Landmarker in VIDEO mode
 * over an uploaded video element. Captures landmarks at ~10fps and
 * returns the frame stream so the calling page can hand it to an analyzer.
 *
 * Does NOT use the camera. Reuses the same MediaPipe library + WASM
 * artifacts as `usePoseDetection3D` (loaded fresh in VIDEO mode so the
 * existing live-tracking session is unaffected).
 */

const MP_VISION_VERSION = "0.10.9";
const POSE_MODEL_URL =
  "https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_lite/float16/1/pose_landmarker_lite.task";
const WASM_BASE = `https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@${MP_VISION_VERSION}/wasm`;

let _mpVisionPromise = null;
const loadMpVision = () => {
  if (_mpVisionPromise) return _mpVisionPromise;
  _mpVisionPromise = (async () => {
    const mod = await import(
      /* webpackIgnore: true */
      `https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@${MP_VISION_VERSION}/vision_bundle.mjs`
    );
    return mod;
  })();
  return _mpVisionPromise;
};

export const useVideoMovementAnalyzer = () => {
  const [phase, setPhase] = useState("idle");          // idle|loading|processing|done|error
  const [progress, setProgress] = useState(0);         // 0..1
  const [error, setError] = useState(null);
  const [frames, setFrames] = useState([]);            // captured per-frame results
  const cancelRef = useRef(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);                      // skeleton overlay canvas

  const drawSkeleton = useCallback((ctx, lm, w, h) => {
    if (!lm) return;
    ctx.clearRect(0, 0, w, h);
    // Bone connections (a subset that's enough for a live look)
    const C = [
      [11, 13], [13, 15], [12, 14], [14, 16],          // arms
      [11, 12], [11, 23], [12, 24], [23, 24],          // torso
      [23, 25], [25, 27], [24, 26], [26, 28],          // legs
      [27, 31], [28, 32],                               // feet
    ];
    ctx.lineWidth = 2.5;
    ctx.strokeStyle = "#CCFF00";
    ctx.fillStyle = "#CCFF00";
    for (const [a, b] of C) {
      const A = lm[a], B = lm[b];
      if (!A || !B) continue;
      if ((A.visibility ?? 0) < 0.4 || (B.visibility ?? 0) < 0.4) continue;
      ctx.beginPath();
      ctx.moveTo(A.x * w, A.y * h);
      ctx.lineTo(B.x * w, B.y * h);
      ctx.stroke();
    }
    for (let i = 0; i < lm.length; i++) {
      const p = lm[i];
      if (!p || (p.visibility ?? 0) < 0.4) continue;
      ctx.beginPath();
      ctx.arc(p.x * w, p.y * h, 3, 0, Math.PI * 2);
      ctx.fill();
    }
  }, []);

  const cancel = useCallback(() => { cancelRef.current = true; }, []);

  const analyze = useCallback(async (videoEl, { fps = 10 } = {}) => {
    cancelRef.current = false;
    setError(null);
    setFrames([]);
    setProgress(0);
    setPhase("loading");

    let mp;
    try {
      mp = await loadMpVision();
    } catch (e) {
      setError("Could not load pose engine.");
      setPhase("error");
      return null;
    }

    let landmarker;
    try {
      const fileset = await mp.FilesetResolver.forVisionTasks(WASM_BASE);
      landmarker = await mp.PoseLandmarker.createFromOptions(fileset, {
        baseOptions: { modelAssetPath: POSE_MODEL_URL, delegate: "GPU" },
        runningMode: "VIDEO",
        numPoses: 1,
        minPoseDetectionConfidence: 0.5,
        minPosePresenceConfidence: 0.5,
        minTrackingConfidence: 0.5,
      });
    } catch (e) {
      setError("Pose detector failed to initialise.");
      setPhase("error");
      return null;
    }

    setPhase("processing");

    // Wait for metadata so duration is known
    if (!videoEl.duration || isNaN(videoEl.duration)) {
      await new Promise(r => {
        const h = () => { videoEl.removeEventListener("loadedmetadata", h); r(); };
        videoEl.addEventListener("loadedmetadata", h);
      });
    }
    // Chrome/WebKit bug: blob URLs from .mov / phone-recorded mp4 often
    // report duration === Infinity until the video has been seeked past
    // its end. Force-resolve it now so the analysis loop knows where to stop.
    if (!isFinite(videoEl.duration) || videoEl.duration <= 0) {
      try {
        await new Promise((resolve) => {
          const onSeek = () => {
            videoEl.removeEventListener("seeked", onSeek);
            // After seeking past the end, the browser populates the real duration.
            // Reset to 0 so the analysis loop starts cleanly.
            try { videoEl.currentTime = 0; } catch (_) { /* ignore */ }
            resolve();
          };
          videoEl.addEventListener("seeked", onSeek);
          try { videoEl.currentTime = 1e7; } catch (_) {
            videoEl.removeEventListener("seeked", onSeek);
            resolve();
          }
        });
      } catch (_) { /* ignore */ }
    }
    const duration = isFinite(videoEl.duration) && videoEl.duration > 0 ? videoEl.duration : 0;
    if (duration <= 0) {
      setError("Could not read the video's duration. Please try a different clip.");
      setPhase("error");
      try { landmarker.close(); } catch (_) { /* ignore */ }
      return null;
    }
    const step = 1 / fps;
    const totalSteps = Math.max(1, Math.floor(duration / step));

    // Canvas sized to the video for skeleton overlay
    const canvas = canvasRef.current;
    if (canvas) {
      canvas.width = videoEl.videoWidth || 480;
      canvas.height = videoEl.videoHeight || 360;
    }
    const ctx = canvas ? canvas.getContext("2d") : null;

    const captured = [];
    let t = 0;
    let stepIdx = 0;
    while (t <= duration + 1e-3 && !cancelRef.current) {
      videoEl.currentTime = t;
      // Wait for the seek to finish so the video frame matches `t`
      await new Promise(r => {
        const onSeek = () => { videoEl.removeEventListener("seeked", onSeek); r(); };
        videoEl.addEventListener("seeked", onSeek);
      });
      try {
        const result = landmarker.detectForVideo(videoEl, Math.round(t * 1000));
        const lm = result?.landmarks?.[0] || null;
        const lm3D = result?.worldLandmarks?.[0] || null;
        captured.push({ t, landmarks2D: lm, landmarks3D: lm3D });
        if (ctx && canvas) drawSkeleton(ctx, lm, canvas.width, canvas.height);
      } catch (_) {
        captured.push({ t, landmarks2D: null, landmarks3D: null });
      }
      stepIdx++;
      setProgress(Math.min(1, stepIdx / totalSteps));
      t += step;
    }

    try { landmarker.close(); } catch (_) { /* ignore */ }

    if (cancelRef.current) {
      setPhase("idle");
      return null;
    }

    setFrames(captured);
    setPhase("done");
    return captured;
  }, [drawSkeleton]);

  return {
    phase, progress, error, frames,
    videoRef, canvasRef,
    analyze, cancel,
  };
};

export default useVideoMovementAnalyzer;
